import { useState, type FormEvent } from 'react'
import { cleanVatRate, parsePriceCents, parseQty } from '@wf/shared'
import { VatSelect } from './VatSelect'

/** Bezeichnung, (optionale SNR), Netto-Preis (ganze Cent), MwSt-Satz und Menge einer Zeile. */
export interface LineDraft {
  name: string
  partNumber?: string | null
  priceCents: number
  vatRate: number
  qty: number
}

/**
 * Inline-Bearbeiten einer Zeile (Teil ODER Arbeits-Position): Bezeichnung, Netto-Preis, MwSt, Menge — bei Teilen
 * zusätzlich die SNR. Geld/Menge/Satz laufen ausschließlich über @wf/shared (parsePriceCents/parseQty/cleanVatRate,
 * ganze Cent, kein Float-Euro). OrderParts und OrderItems teilen sich dieses Formular (EINE Eingabe-Wahrheit).
 * `label` macht die aria-Labels eindeutig, wenn beide Listen gleichzeitig auf dem Detail stehen.
 * `withPartNumber` blendet das SNR-Feld ein (nur für Teile).
 */
export function LineEditForm({
  initial,
  label,
  busy,
  withPartNumber = false,
  onSave,
  onCancel,
}: {
  initial: LineDraft
  label: string
  busy: boolean
  withPartNumber?: boolean
  onSave: (draft: LineDraft) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initial.name)
  const [partNumber, setPartNumber] = useState(initial.partNumber ?? '')
  const [price, setPrice] = useState((initial.priceCents / 100).toFixed(2).replace('.', ','))
  const [vatRate, setVatRate] = useState(cleanVatRate(initial.vatRate))
  const [qty, setQty] = useState(String(initial.qty))

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!name.trim()) return
    onSave({
      name: name.trim(),
      ...(withPartNumber ? { partNumber: partNumber.trim() || null } : {}),
      priceCents: parsePriceCents(price),
      vatRate: cleanVatRate(vatRate),
      qty: parseQty(qty),
    })
  }

  return (
    <form className="wf-line-add wf-line-edit" onSubmit={submit}>
      <input className="wf-input" aria-label={`${label} bearbeiten: Bezeichnung`} value={name} onChange={(event) => setName(event.target.value)} />
      {withPartNumber ? (
        <input
          className="wf-input"
          aria-label={`${label} bearbeiten: SNR`}
          placeholder="SNR"
          value={partNumber}
          onChange={(event) => setPartNumber(event.target.value)}
        />
      ) : null}
      <input
        className="wf-input wf-tnum"
        inputMode="decimal"
        aria-label={`${label} bearbeiten: Nettopreis`}
        value={price}
        onChange={(event) => setPrice(event.target.value)}
      />
      <VatSelect value={vatRate} onChange={(r) => setVatRate(cleanVatRate(r))} label={`${label} bearbeiten: MwSt`} />
      <input
        className="wf-input wf-tnum wf-line__qty"
        inputMode="numeric"
        aria-label={`${label} bearbeiten: Menge`}
        value={qty}
        onChange={(event) => setQty(event.target.value)}
      />
      {/* Eindeutiger Accessible-Name: auf dem Detail steht parallel der „Speichern"-Knopf der Auftragsdaten. */}
      <button type="submit" className="wf-btn wf-btn--ghost" aria-label={`${label} speichern`} disabled={busy || !name.trim()}>
        Speichern
      </button>
      <button type="button" className="wf-btn wf-btn--ghost" disabled={busy} onClick={onCancel}>
        Abbrechen
      </button>
    </form>
  )
}
